
import Vue from 'vue'

import {Component, Prop} from 'vue-property-decorator'
import {TranslateResult} from 'vue-i18n'
import moment from "moment";
import {getLocale} from "@/services/system";
import {usePiniaStore} from "@/store/piniaStore";
import {RatingModel} from "@/model/RatingModel";
import {RatingPayload} from "@/types/payload";


@Component
export default class RatingDialog extends Vue {
  private showError: boolean = false
  private errorMessage: TranslateResult | null = null
  private saveInProgress: boolean = false
  private snackbar: boolean = false
  private snackBarText: TranslateResult = ''
  private snackbarColor: string = ''
  private ratingValue: any | null = null
  private description: string | null = null
  private piniaStore: any = usePiniaStore()
  private selectedId: string | null = null
  private offerRating: RatingModel | null = null
  private createdDate: string = ''
  private isRatingFinished: boolean = false

  @Prop({type: Boolean, default: false}) readonly show!: boolean
  @Prop({type: Array, default: false}) readonly lastReservationDetailData!: any[]
  @Prop({type: String, required: false}) readonly date!: string
  @Prop({type: Object, required: false}) readonly selectedOfferRating!: RatingModel
  @Prop({type: Array, required: false}) readonly ratingDataList!: any
  @Prop({type: Number, required: false}) readonly selectedRatingValue!: null
  @Prop({type: Object, required: false}) readonly selectedOffer!: any
  @Prop({type: Array, required: false}) readonly itemData!: any[]
  @Prop({type: Number, required: false}) readonly ratingDeliveryTimeData!: null

  get isSaveButtonDisabled ():boolean {
    let isDisabled: boolean = false
    if(this.ratingValue) {
      isDisabled = false
    } else {
      isDisabled = true
    }
    return isDisabled
  }

  setOpacity (value:any): any {
    this.ratingValue = value
  }

  getAmount (item:any) {
    if(item) {
      return item + 'x'
    } else {
      return ''
    }
  }

  get getFormattedDate (): string {
    let date: string = ''
    if(this.lastReservationDetailData && this.lastReservationDetailData.length > 0) {
      this.lastReservationDetailData.forEach((item:any) => {
        date = item.date
      })
    } else {
      date = this.selectedOffer.date
    }
    return moment(date)
      .locale(getLocale())
      .format('D.M.YYYY').toLocaleLowerCase()
  }

  get getFilteredData(): any {
    const groupedData:any = {}

    if(this.selectedOffer) {
        this.selectedOffer.reservedOffers.forEach((offer:any) => {
          const tagId = offer.dailyMenuOffer.referenceFoodItem.tags[0].id
          const title = offer.dailyMenuOffer.referenceFoodItem.title
          const amount = offer.amount
          const group = offer.dailyMenuOffer.menuGroupSlot.tags.find((tag:any) => tag.id ===  tagId)
          const groupName = group && group.title ? group.title : ""
          if (!groupedData[groupName]) {
            groupedData[groupName] = {
              group: groupName
            }
          }
          if (!groupedData[groupName][title]) {
            groupedData[groupName][title] = {
              group: groupName,
              title: title,
              amount: amount
            }
          } else {
            groupedData[groupName][title].amount += amount
          }
        })
    } else {
      this.lastReservationDetailData.forEach((item:any) => {
        item.reservedOffers.forEach((offer: any) => {
          const tagId = offer.dailyMenuOffer.referenceFoodItem.tags[0].id
          const title = offer.dailyMenuOffer.referenceFoodItem.title
          const amount = offer.amount
          const group = item.topMenuGroup.subMenuGroups.find((group:any) => group.tags.some((tag:any) => tag.id === tagId))
          const groupName = group && group.title ? group.title : ""
          if (!groupedData[groupName]) {
            groupedData[groupName] = {
              group: groupName,
            }
          }
          if (!groupedData[groupName][title]) {
            groupedData[groupName][title] = {
              group: groupName,
              title: title,
              amount: amount
            };
          } else {
            groupedData[groupName][title].amount += amount
          }
        })
      })
    }

    return groupedData
  }

  closeDialog (): void {
    this.$emit('close')
  }

  sendRating (): void {
    if(this.selectedId === null) {
      this.createRatingForOrder()
    } else {
      this.onUpdateSelectedRating()
    }
  }

  get isDateTwoDaysBeforeToday(): boolean {
    let canBeRated:boolean = false

    if(this.lastReservationDetailData && this.lastReservationDetailData.length > 0) {
      const offerReservation = this.lastReservationDetailData[0]
      const deliveryTimeString = offerReservation.offerReservationDelivery?.createdAt

      if (deliveryTimeString) {
        const now = moment()
        const deliveryTime = moment(deliveryTimeString)
        const differenceInHours = deliveryTime.diff(now, 'hours')
        const delta = this.ratingDeliveryTimeData

        if (delta && differenceInHours <= delta) {
          canBeRated = true
        }
      }
    }

    return canBeRated
  }

  async createRatingForOrder(): Promise<void> {
    let reservationIdList:any[] = []
    if(this.selectedOffer) {
      this.selectedOffer.reservedOffers!.forEach((item:any) => {
        item!.reservationIds.forEach((a:any) => {
          reservationIdList.push(`/api/food-ordering/offer-reservation/${a}`)
        })
      })
    } else {
      this.lastReservationDetailData.forEach((item:any) => {
        reservationIdList.push(`/api/food-ordering/offer-reservation/${item.id}`)
      })
    }

    const payload: any = {
      offerReservations: reservationIdList,
      value: this.ratingValue,
      note: this.description ? this.description : null
    }
    this.saveInProgress = true
    try {
      const response = await this.piniaStore.createNewRating(payload)
      this.isRatingFinished = true
      this.offerRating = response


    } catch (e) {
      this.snackbarColor = "error"
      this.snackBarText = this.$t('ratingDialog.errorDataCreate')
      this.snackbar = true
    } finally {
      this.saveInProgress = false
    }
  }

  async onUpdateSelectedRating(): Promise<void> {
    if(this.isDateTwoDaysBeforeToday) {
      const payload:RatingPayload = {
        id: this.selectedId,
        boarder: this.selectedOfferRating ? this.selectedOfferRating.boarder : this.ratingDataList[0].boarder,
        value: this.ratingValue,
        note: this.description ? this.description : null,
        offerReservations: this.selectedOfferRating ? this.selectedOfferRating.offerReservations : this.ratingDataList[0].offerReservations
      }
      this.saveInProgress = true
      try {
        const response = await this.piniaStore.updateSelectedRating(payload)
        this.offerRating = response
        this.isRatingFinished = true

      } catch (e) {
        this.snackbarColor = "error"
        this.snackBarText = this.$t('ratingDialog.errorDataChange')
        this.snackbar = true
      } finally {
        this.saveInProgress = false
      }
    }
  }

  created() {
    if(this.selectedOfferRating) {
      this.ratingValue = this.selectedOfferRating.value
      this.selectedId = this.selectedOfferRating.id
      this.description = this.selectedOfferRating.note ? this.selectedOfferRating.note : null
    } else if(this.selectedOffer) {
      let foundItem: RatingModel | undefined = this.ratingDataList.find((i:RatingModel) => i.offerReservations.includes(`/api/food-ordering/offer-reservation/${this.selectedOffer.id}`))
      this.ratingValue = foundItem ? foundItem.value : null
      this.selectedId = foundItem ? foundItem.id : null
      this.description = foundItem ? foundItem.note : null
      this.createdDate = foundItem ? foundItem.createdAt : ''
    }
  }

}
